import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "services/api";
import { subRequest } from "services";
import { convertParamsToString, chatOnWhatsApp } from "utils/helpers";
import i18n from "i18n";

export type DurationValue = "SEMI_ANNUAL" | "ANNUAL" | "MONTH" | "QUARTERLY";
export type DeviceType = "android" | "ios";
type SubType = {
  id: string;
  name: string;
  durationType: DurationValue;
};

const DURATION_CATALOG: Array<SubType> = [
  {
    id: "firstId123",
    name: "Annually",
    durationType: "ANNUAL",
  },
  {
    id: "secondId123",
    name: "6-Months",
    durationType: "SEMI_ANNUAL",
  },
  {
    id: "quarterlyId123",
    name: "Quarterly",
    durationType: "QUARTERLY",
  },
  {
    id: "thirdId123",
    name: "Monthly",
    durationType: "MONTH",
  },
];

export function useSubscriptionData(country: string | null, deviceParam: string | null) {
  const [durationType, setDurationType] = useState<DurationValue>("SEMI_ANNUAL");
  const [device, setDevice] = useState<DeviceType>("android");
  const [url, setUrl] = useState("");

  useEffect(() => {
    let subUrl = "";
    switch (country) {
      case "ng":
        subUrl = convertParamsToString(subRequest.SUB_PLAN, { country:country.toLowerCase() });
        setUrl(subUrl);
        break;
      case "gh":
        subUrl = convertParamsToString(subRequest.SUB_PLAN, { country: device === "ios" ? "US" : country.toLowerCase() });
        setUrl(subUrl);
        break;
      case "cd":
        subUrl = convertParamsToString(subRequest.SUB_PLAN, { country:country.toLowerCase() });
        setUrl(subUrl);
        break;
      default:
        subUrl = convertParamsToString(subRequest.SUB_PLAN, { country: "US" });
        setUrl(subUrl);
        break;
    }
  }, [country, device]);

  useEffect(() => {
    if (deviceParam) {
      if (deviceParam.toLowerCase() === "ios") setDevice("ios");
    } else {
      setDevice("android");
    }
  }, [deviceParam]);

  const getAPICall = async ({ queryKey }: any) => {
    const url = queryKey[1];
    const { data } = await axiosInstance.get(url);
    return data;
  };
  const whatsaAppHandle = () => {
    const ghana = "+233545750476";
    const nigeria = "  +2349130000118";
    let whatapp = ghana;
    if (country === "ng") {
      whatapp = nigeria;
    }
    chatOnWhatsApp(i18n.t("common:whatsappHelpMessage"), whatapp);
  };
  const { isLoading, isError, data } = useQuery(["GET_SUB_API", url], getAPICall, {
    enabled: url !== "",
    staleTime: 3 * (60 * 1000), // 3 mins
  });

  const availableDurationTypes = useMemo(() => {
    const plans = data?.data ?? [];
    return new Set<string>(
      plans.map((item: { durationType: string }) => item.durationType)
    );
  }, [data]);

  const subscriptionType = useMemo(() => {
    if (availableDurationTypes.size === 0) {
      return DURATION_CATALOG.filter((tab) => tab.durationType !== "QUARTERLY");
    }
    return DURATION_CATALOG.filter((tab) =>
      availableDurationTypes.has(tab.durationType)
    );
  }, [availableDurationTypes]);

  useEffect(() => {
    if (availableDurationTypes.size === 0) return;
    if (!availableDurationTypes.has(durationType)) {
      const fallback = DURATION_CATALOG.find((tab) =>
        availableDurationTypes.has(tab.durationType)
      );
      if (fallback) {
        setDurationType(fallback.durationType);
      }
    }
  }, [availableDurationTypes, durationType]);

  return { isLoading, isError, data, durationType, setDurationType, device, subscriptionType, whatsaAppHandle } as const;
}
